package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.Node
import kotlin.random.Random

/** Surface for the nonmodal RGB picker, constrained to fit narrow properties panels. */
val LColorFieldPopoverStyle = CssStyle.base {
    Modifier.backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .borderRadius(Radius.Control).padding(Space.MD).styleModifier {
            property("position", "absolute")
            property("top", "100%")
            property("left", "0")
            property("z-index", "50")
            property("width", "min(280px, calc(100vw - 32px))")
            property("box-sizing", "border-box")
            property("display", "grid")
            property("gap", "12px")
        }
}

/** Neutral 40 px picker buttons; selected color is content inside the button. */
val LColorFieldButtonStyle = CssStyle {
    base {
        Modifier.height(ControlHeight.MD)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control).padding(leftRight = Space.MD)
            .fontSize(TypeScale.BodySmall).styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "8px")
                property("cursor", "pointer")
            }
    }
    focusVisible {
        Modifier.outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    cssRule(":disabled") { Modifier.styleModifier { property("cursor", "default"); property("opacity", "0.5") } }
}

/**
 * A 40 px swatch trigger opening a keyboard-accessible, nonmodal RGB picker.
 * Null renders Mixed. Swatches and the native color input commit complete colors immediately;
 * hex drafts commit on Enter or blur only after strict #RRGGBB validation. Escape closes the picker
 * and returns focus to its trigger; outside interaction closes without stealing that interaction.
 */
@Composable
fun LColorField(
    value: String?,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    swatches: List<String> = emptyList(),
    enabled: Boolean = true,
) {
    val rootId = remember { "lingvo-color-${Random.nextInt(1_000_000)}" }
    val triggerId = "$rootId-trigger"
    val hexId = "$rootId-hex"
    val popoverId = "$rootId-popover"
    val errorId = "$rootId-error"
    val canonical = value?.let(LColorFieldModel::commit)
    var committed by remember { mutableStateOf(canonical) }
    var draft by remember { mutableStateOf(canonical.orEmpty()) }
    var dirty by remember { mutableStateOf(false) }
    var invalid by remember { mutableStateOf(false) }
    var open by remember { mutableStateOf(false) }
    val currentEnabled by rememberUpdatedState(enabled)
    val currentChange by rememberUpdatedState(onValueChange)
    fun close(returnFocus: Boolean) {
        if (returnFocus) {
            dirty = false
            invalid = false
            draft = committed.orEmpty()
        }
        open = false
        if (returnFocus) (document.getElementById(triggerId) as? HTMLElement)?.focus()
    }
    fun accept(color: String) {
        if (!currentEnabled) return
        committed = color
        draft = color
        dirty = false
        invalid = false
        currentChange(color)
    }
    fun commit() {
        if (!currentEnabled || !dirty) return
        val color = LColorFieldModel.commit(draft)
        if (color == null) invalid = true else accept(color)
    }
    LaunchedEffect(canonical, enabled) {
        committed = canonical
        if (!dirty || !enabled) {
            draft = canonical.orEmpty()
            dirty = false
            invalid = false
        }
        if (!enabled) open = false
    }
    LaunchedEffect(open) {
        if (open) {
            draft = committed.orEmpty()
            dirty = false
            invalid = false
            (document.getElementById(hexId) as? HTMLElement)?.focus()
        }
    }
    DisposableEffect(open) {
        val outside: (Event) -> Unit = { event ->
            if (open && document.getElementById(rootId)?.contains(event.target as? Node) != true) close(false)
        }
        val key: (Event) -> Unit = { event ->
            if (open && (event as KeyboardEvent).key == "Escape") {
                event.preventDefault()
                close(true)
            }
        }
        if (open) {
            document.addEventListener("pointerdown", outside)
            document.addEventListener("focusin", outside)
            document.addEventListener("keydown", key)
        }
        onDispose {
            document.removeEventListener("pointerdown", outside)
            document.removeEventListener("focusin", outside)
            document.removeEventListener("keydown", key)
        }
    }
    Column(LFieldGroupStyle.toModifier().then(modifier).position(Position.Relative)
        .attr("id", rootId).attr("data-disabled", (!enabled).toString())) {
        Label(forId = triggerId, attrs = LFieldLabelStyle.toModifier().toAttrs()) { Text(label) }
        Button(LColorFieldButtonStyle.toModifier().width(100.percent).toAttrs {
            id(triggerId)
            type(ButtonType.Button)
            attr("data-color-trigger", "")
            attr("data-disabled", (!enabled).toString())
            attr("aria-label", "$label: ${committed ?: tr("ds.ds.lcolor_field.mixed", "Mixed")}")
            attr("aria-haspopup", "dialog")
            attr("aria-expanded", (open && enabled).toString())
            if (open && enabled) attr("aria-controls", popoverId)
            if (!enabled) disabled()
            onClick { if (currentEnabled) { if (open) close(false) else open = true } }
        }) {
            ColorChip(committed)
            Text(committed ?: t("ds.ds.lcolor_field.mixed", "Mixed"))
        }
        if (open && enabled) {
            Div(LColorFieldPopoverStyle.toModifier().toAttrs {
                id(popoverId)
                attr("role", "dialog")
                attr("aria-label", trf("ds.color_field.picker", "Choose {label}", "label" to label))
            }) {
                val colors = LColorFieldModel.swatches(swatches)
                if (colors.isNotEmpty()) Div(Modifier.styleModifier {
                    property("display", "flex"); property("flex-wrap", "wrap"); property("gap", "8px")
                }.toAttrs()) {
                    colors.forEach { color ->
                        Button(LColorFieldButtonStyle.toModifier().width(ControlHeight.MD).padding(0.px).toAttrs {
                            type(ButtonType.Button)
                            attr("data-color-swatch", color)
                            attr("aria-label", trf("ds.color_field.use_swatch", "Use {color}", "color" to color))
                            attr("aria-pressed", (color == committed).toString())
                            onClick { accept(color) }
                        }) { ColorChip(color) }
                    }
                }
                Label(forId = "$rootId-native", attrs = LFieldLabelStyle.toModifier().toAttrs()) {
                    Text(t("ds.ds.lcolor_field.custom_color", "Custom color"))
                }
                Input(InputType.Color, attrs = Modifier.height(ControlHeight.MD).width(100.percent).toAttrs {
                    id("$rootId-native")
                    value(committed ?: "#000000")
                    onInput { event -> LColorFieldModel.commit(event.value)?.let(::accept) }
                })
                Label(forId = hexId, attrs = LFieldLabelStyle.toModifier().toAttrs()) {
                    Text(t("ds.ds.lcolor_field.hex_color", "Hex color"))
                }
                Div(LTextFieldFrameStyle.toModifier().toAttrs()) {
                    Input(InputType.Text, attrs = LTextFieldInputStyle.toModifier().toAttrs {
                        id(hexId)
                        value(draft)
                        placeholder(tr("ds.ds.lcolor_field.mixed", "Mixed"))
                        attr("maxlength", "7")
                        attr("spellcheck", "false")
                        attr("autocomplete", "off")
                        if (invalid) { attr("aria-invalid", "true"); attr("aria-describedby", errorId) }
                        onInput { if (currentEnabled) { draft = it.value; dirty = true; invalid = false } }
                        onBlur { commit() }
                        onKeyDown { event ->
                            when (event.key) {
                                "Enter" -> { event.preventDefault(); commit() }
                                "Escape" -> { event.preventDefault(); event.stopPropagation(); close(true) }
                            }
                        }
                    })
                }
                if (invalid) Div(LFieldErrorStyle.toModifier().toAttrs {
                    id(errorId)
                    attr("role", "alert")
                }) { Text(t("ds.ds.lcolor_field.enter_a_six_digit_hex_color", "Enter a six-digit hex color, such as #AABBCC.")) }
                LButton(t("ds.ds.lcolor_field.close", "Close"), onClick = { close(true) }, variant = LButtonVariant.SECONDARY)
            }
        }
    }
}

@Composable
private fun ColorChip(color: String?) {
    val palette = ColorMode.current.toSitePalette()
    Span(Modifier.width(IconSize.XL).height(IconSize.XL)
        .border(1.px, LineStyle.Solid, palette.borderStrong).styleModifier {
        property("display", "inline-block")
        property("flex-shrink", "0")
        property("border-radius", "2px")
        if (color != null) property("background-color", color)
        else property("background", "repeating-conic-gradient(${palette.borderStrong} 0% 25%, ${palette.surfaceSunken} 0% 50%) 0 / 8px 8px")
    }.toAttrs { attr("aria-hidden", "true") })
}
